import { components } from '@common/types';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { t } from 'i18next';
import { CloudDone, Warning } from '@mui/icons-material';
import { useRequirementsModal } from './hooks';
import { RequirementItem } from 'components/DynamicFormFields';
import { useQueryClient } from '@tanstack/react-query';
import { theme } from '@store';
import { CustomModal } from '@components';

const hideButtonList = ['Current Challenges'];

export const RequirementsModal = ({
  button_text,
  requirements,
  title,
  description,
  disable_if,
  patient_id,
  disabled = false,
  handleChangeField,
  missing_fields = [],
  isSavingTask,
}: components['schemas']['TaskRequirementModal'] & {
  isSavingTask: boolean;
  missing_fields: string[];
  patient_id?: string;
  disabled?: boolean;
  handleChangeField?: (nameList: string[]) => void;
}) => {
  const queryClient = useQueryClient();
  const { handleClose, handleOpen, isDisabled, isOpen, formIsInvalid } =
    useRequirementsModal({
      missing_fields,
      requirements,
      disable_if,
      disabled,
      onOpen: () => {
        queryClient.refetchQueries({ queryKey: ['task', patient_id] });
      },
    });

  const isHidden =
    isDisabled && hideButtonList.includes(button_text) ? 'none' : 'flex';

  return (
    <>
      <Box
        sx={{
          display: isHidden,
          width: '100%',
          gap: 2,
        }}
      >
        <Button
          variant='outlined'
          sx={{
            fontSize: '0,875rem',
            fontWeight: 'medium',
            lineHeight: '24px',
          }}
          onClick={handleOpen}
          disabled={isDisabled}
        >
          {button_text}
        </Button>

        {formIsInvalid && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color='error' />
            <Typography variant='body2' sx={{ color: 'error' }}>
              {t('general.incompleteData')}
            </Typography>
          </Box>
        )}
      </Box>

      <CustomModal
        isOpen={isOpen && !disabled}
        onClose={handleClose}
        title={title}
        isLoading={isSavingTask}
      >
        {description && (
          <Box sx={{ width: '100%' }}>
            <Typography
              variant='body2'
              sx={{
                color: theme => theme.palette.semiBlack.main,
                pb: '1rem',
              }}
            >
              {description}
            </Typography>
          </Box>
        )}

        {requirements.map((req, index) => (
          <Box key={`${req.title} ${index}`} sx={{ mb: 1.5 }}>
            <RequirementItem
              {...req}
              patient_id={patient_id}
              handleChangeField={handleChangeField}
            />
          </Box>
        ))}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            position: 'sticky',
            bottom: 0,
            p: '1rem 0',
            zIndex: 3,
            backgroundColor: theme.palette.common.white,
            gap: 2,
          }}
        >
          {isSavingTask ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <CircularProgress
                size='24px'
                sx={{ color: 'rgba(0, 0, 0, 0.12)' }}
              />
              <Typography sx={{ color: 'text.disabled' }}>
                {t('general.isSaving')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <CloudDone
                sx={{ height: '24px', width: '32px' }}
                color='disabled'
              />
              <Typography sx={{ color: 'text.disabled' }}>
                {t('general.saved')}
              </Typography>
            </Box>
          )}
          <Button
            size='large'
            disabled={isSavingTask}
            onClick={handleClose}
            color='secondary'
            variant='contained'
            sx={{ mr: 1, color: theme.palette.common.white }}
          >
            {t('general.confirm')}
          </Button>
        </Box>
      </CustomModal>
    </>
  );
};
